/**
 * TS-11.4 — supervisor delegation service.
 *
 * Backed by migration 00176 `supervisor_delegations`. Defensive — when
 * the table doesn't exist yet on a Supabase deploy, list returns []
 * so the supervisor-chain resolver falls back to static
 * worker_attributes.supervisor_id.
 */
import { supabase } from '@/lib/supabase'
import type { SupervisorDelegation } from '../types/manufacturing-time.types'

type DbDelegation = {
  id: string
  delegator_user_id: string
  delegate_user_id: string
  effective_from: string
  effective_to: string
  reason: string | null
  created_by: string | null
  created_at: string
  revoked_at: string | null
  revoked_by: string | null
  revoke_reason: string | null
  source_pto_request_id?: string | null
}

// source_pto_request_id is added by migration 00385; selects below tolerate its
// absence (retry without it) so a pre-migration deploy still reads delegations.
const COLS_BASE =
  'id, delegator_user_id, delegate_user_id, effective_from, effective_to, reason, created_by, created_at, revoked_at, revoked_by, revoke_reason'
const COLS = `${COLS_BASE}, source_pto_request_id`

function dbToApp(row: DbDelegation): SupervisorDelegation {
  return {
    id: row.id,
    delegatorUserId: row.delegator_user_id,
    delegateUserId: row.delegate_user_id,
    effectiveFrom: row.effective_from,
    effectiveTo: row.effective_to,
    reason: row.reason ?? undefined,
    createdBy: row.created_by ?? undefined,
    createdAt: row.created_at,
    revokedAt: row.revoked_at ?? undefined,
    revokedBy: row.revoked_by ?? undefined,
    revokeReason: row.revoke_reason ?? undefined,
    sourcePtoRequestId: row.source_pto_request_id ?? null,
  }
}

/** All delegations (active + future + expired + revoked) — admin grid. */
export async function listAllDelegations(): Promise<SupervisorDelegation[]> {
  const run = (cols: string) =>
    supabase.from('supervisor_delegations').select(cols).order('effective_from', { ascending: false })
  let { data, error } = await run(COLS)
  if (error) ({ data, error } = await run(COLS_BASE)) // pre-00381: drop source_pto_request_id
  if (error) {
    console.warn('[supervisorDelegationService] listAll failed, returning empty:', error.message)
    return []
  }
  return ((data ?? []) as unknown as DbDelegation[]).map(dbToApp)
}

/**
 * Active + future only — hot path for supervisor-chain resolution.
 * Returns rows with revoked_at IS NULL and effective_to > now.
 */
export async function listActiveOrFutureDelegations(): Promise<SupervisorDelegation[]> {
  const nowIso = new Date().toISOString()
  const run = (cols: string) =>
    supabase.from('supervisor_delegations').select(cols).is('revoked_at', null).gt('effective_to', nowIso)
  let { data, error } = await run(COLS)
  if (error) ({ data, error } = await run(COLS_BASE)) // pre-00381: drop source_pto_request_id
  if (error) {
    console.warn('[supervisorDelegationService] listActiveOrFuture failed, returning empty:', error.message)
    return []
  }
  return ((data ?? []) as unknown as DbDelegation[]).map(dbToApp)
}

export interface CreateDelegationInput {
  delegatorUserId: string
  delegateUserId: string
  effectiveFrom: string
  effectiveTo: string
  reason?: string
  createdBy?: string
  /** Set when created from an approved PTO window (Time Approval Routing P3). */
  sourcePtoRequestId?: string | null
}

export async function createDelegation(input: CreateDelegationInput): Promise<void> {
  const row: Record<string, unknown> = {
    delegator_user_id: input.delegatorUserId,
    delegate_user_id: input.delegateUserId,
    effective_from: input.effectiveFrom,
    effective_to: input.effectiveTo,
    reason: input.reason ?? null,
    created_by: input.createdBy ?? null,
  }
  if (input.sourcePtoRequestId) row.source_pto_request_id = input.sourcePtoRequestId
  let { error } = await supabase.from('supervisor_delegations').insert(row)
  if (error && input.sourcePtoRequestId) {
    // Pre-00381: source_pto_request_id column absent — retry without it.
    delete row.source_pto_request_id
    ;({ error } = await supabase.from('supervisor_delegations').insert(row))
  }
  if (error) throw new Error(`createDelegation failed: ${error.message}`)
}

export interface RevokeDelegationInput {
  id: string
  revokedBy?: string
  reason?: string
}

export async function revokeDelegation(input: RevokeDelegationInput): Promise<void> {
  const { error } = await supabase
    .from('supervisor_delegations')
    .update({
      revoked_at: new Date().toISOString(),
      revoked_by: input.revokedBy ?? null,
      revoke_reason: input.reason ?? null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', input.id)
  if (error) throw new Error(`revokeDelegation failed: ${error.message}`)
}
